import { useEffect, useState } from 'react';

import { useIdpLegalAgreementsQuery } from '@/entities/dosespot-idp';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';
import { Button } from '@/shared/ui/shadcn/button';
import { Field, FieldLabel } from '@/shared/ui/shadcn/field';
import { Input } from '@/shared/ui/shadcn/input';

import { WizardStepSkeleton } from './_shared/WizardStepSkeleton';
import { DS_STEP_FORM_ID } from './_shared/dsStepFormId';

export interface IdpLegalAgreementsStepProps {
  pendingAgreementIds: readonly number[];
  onSubmit: (nameSigned: string) => void;
  isSubmitting: boolean;
  submitError: Error | null;
  onReadyChange: (ready: boolean) => void;
}

export function IdpLegalAgreementsStep(props: IdpLegalAgreementsStepProps) {
  const { pendingAgreementIds, onSubmit, isSubmitting, submitError, onReadyChange } = props;
  const [nameSigned, setNameSigned] = useState<string>('');

  const agreementsQuery = useIdpLegalAgreementsQuery();
  const agreements = agreementsQuery.data ?? [];

  const displayAgreements =
    pendingAgreementIds.length > 0
      ? agreements.filter(
          (a) => a.AgreementId != null && pendingAgreementIds.includes(a.AgreementId)
        )
      : agreements;

  // The state machine told us which AgreementIds are still pending acceptance.
  // If the API didn't return any of them (e.g., a race between the state-machine
  // write and the fetch, or a stale cache), we cannot show the prescriber
  // anything to sign and must block.
  const hasMissingAgreements: boolean =
    pendingAgreementIds.length > 0 && displayAgreements.length === 0;

  // DoseSpot's official IDP workflow requires the full legal text to be
  // displayed verbatim before the prescriber can accept it. If the API ever
  // returns an empty/missing AgreementText for a required agreement, we must
  // block progression rather than letting the prescriber sign blindly.
  const allAgreementsHaveText: boolean =
    displayAgreements.length > 0 &&
    displayAgreements.every((a) => (a.AgreementText ?? '').trim().length > 0);

  const isSignatureValid: boolean = nameSigned.trim().length >= 2;
  const isReady: boolean = isSignatureValid && !hasMissingAgreements && allAgreementsHaveText;

  useEffect(() => {
    onReadyChange(isReady);
  }, [isReady, onReadyChange]);

  const handleAccept = () => {
    if (!isReady || isSubmitting) {
      return;
    }
    onSubmit(nameSigned.trim());
  };

  if (agreementsQuery.isLoading) {
    return <WizardStepSkeleton />;
  }

  if (agreementsQuery.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Could not load legal agreements</AlertTitle>
        <AlertDescription>
          <div className="flex flex-col gap-2">
            <div>
              {agreementsQuery.error?.message ??
                'The legal agreements could not be fetched. Please try again.'}
            </div>
            <div>
              <Button
                type="button"
                variant="secondary"
                onClick={() => void agreementsQuery.refetch()}
              >
                Retry
              </Button>
            </div>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  if (hasMissingAgreements) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Required legal agreements not found</AlertTitle>
        <AlertDescription>
          <div className="flex flex-col gap-2">
            <div>
              We expected to show you {pendingAgreementIds.length} legal agreement
              {pendingAgreementIds.length === 1 ? '' : 's'} to sign, but DoseSpot didn&apos;t return
              them. This is usually transient. Please retry, or contact support if this keeps
              happening.
            </div>
            <div>
              <Button
                type="button"
                variant="secondary"
                onClick={() => void agreementsQuery.refetch()}
              >
                Retry
              </Button>
            </div>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  if (!allAgreementsHaveText) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Legal agreement text is unavailable</AlertTitle>
        <AlertDescription>
          <div className="flex flex-col gap-2">
            <div>
              The required legal agreement text was not returned by DoseSpot, so we can&apos;t ask
              you to sign it yet. Please retry, or contact support if this keeps happening.
            </div>
            <div>
              <Button
                type="button"
                variant="secondary"
                onClick={() => void agreementsQuery.refetch()}
              >
                Retry
              </Button>
            </div>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <form
      id={DS_STEP_FORM_ID}
      onSubmit={(e) => {
        e.preventDefault();
        handleAccept();
      }}
      className="flex h-full flex-col"
    >
      {submitError ? (
        <Alert variant="destructive" className="mb-4 shrink-0">
          <AlertTitle>Could not accept legal agreements</AlertTitle>
          <AlertDescription>{submitError.message}</AlertDescription>
        </Alert>
      ) : null}

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto">
        {displayAgreements.map((agreement) => (
          <div key={agreement.AgreementId ?? 'unknown'}>
            <p className="whitespace-pre-wrap text-xs leading-relaxed text-muted-foreground">
              {agreement.AgreementText}
            </p>
          </div>
        ))}
      </div>

      <Field className="shrink-0 pt-4">
        <FieldLabel htmlFor="name-signed">
          Type your full legal name to sign these agreements
        </FieldLabel>
        <Input
          id="name-signed"
          type="text"
          placeholder="Your full legal name"
          value={nameSigned}
          onChange={(e) => setNameSigned(e.target.value)}
          autoComplete="name"
        />
      </Field>
    </form>
  );
}
